/* Admin dashboard for notification logs (Resend + Twilio).
 * Route: /admin/notifications  — admin only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { notifLogsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Bell, EnvelopeSimple, ChatCircleText, Warning, ChartLine, Trash, ArrowClockwise, PaperPlaneTilt } from '@phosphor-icons/react';

const CHANNELS = [
  { key: '', label: 'Tous', icon: Bell },
  { key: 'email', label: 'Email', icon: EnvelopeSimple },
  { key: 'sms', label: 'SMS', icon: ChatCircleText },
];

const STATUSES = [
  { key: '', label: 'Tous' },
  { key: 'sent', label: 'Envoyés', color: 'text-emerald-700 bg-emerald-100' },
  { key: 'failed', label: 'Échecs', color: 'text-red-700 bg-red-100' },
];

const AdminNotifications = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    channel: '', status: '', event: '', to: '',
    date_from: '', date_to: '',
    limit: 50, skip: 0,
  });
  const [pruning, setPruning] = useState(false);
  const [retentionDays, setRetentionDays] = useState(90);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        notifLogsApi.stats(30),
        notifLogsApi.list(Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined),
        )),
      ]);
      setStats(s.data);
      setLogs(l.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const runPrune = async () => {
    if (!window.confirm(`Supprimer définitivement les logs de plus de ${retentionDays} jours ?`)) return;
    setPruning(true);
    try {
      const { data } = await notifLogsApi.prune(retentionDays);
      toast.success(`${data.deleted} entrée(s) supprimée(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setPruning(false); }
  };

  const totalRate = stats?.all?.success_rate;

  return (
    <div className="space-y-6" data-testid="admin-notifications">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">Journal des notifications</h1>
          <p className="text-sm text-[#64748B] mt-1">
            Historique des envois email &amp; SMS · 30 derniers jours
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm("Envoyer le digest hebdomadaire à tous les admins maintenant ?")) return;
              try {
                const { data } = await api.post('/weekly-digest/send');
                if (data.sent_count > 0) {
                  toast.success(`Digest envoyé à ${data.sent_count} admin(s).`);
                } else {
                  toast.warning(`Aucun email envoyé (${data.errors || 0} erreur(s)).`);
                }
                load();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Erreur envoi digest');
              }
            }}
            className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF]/5"
            data-testid="send-weekly-digest-btn"
          >
            <PaperPlaneTilt size={14} className="mr-2" weight="duotone" /> Envoyer le digest hebdo
          </Button>
          <Button variant="outline" onClick={load} data-testid="notif-reload-btn">
            <ArrowClockwise size={14} className="mr-2" /> Actualiser
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total envois" value={stats?.all?.total ?? '—'} sub={`${stats?.all?.sent ?? 0} réussis`} icon={Bell} color="#0052FF" testid="kpi-total" />
        <KpiCard label="Emails" value={stats?.email?.total ?? '—'} sub={`Taux ${stats?.email?.success_rate ?? '—'}%`} icon={EnvelopeSimple} color="#10B981" testid="kpi-email" />
        <KpiCard label="SMS" value={stats?.sms?.total ?? '—'} sub={`Taux ${stats?.sms?.success_rate ?? '—'}%`} icon={ChatCircleText} color="#F59E0B" testid="kpi-sms" />
        <KpiCard
          label="Taux global"
          value={totalRate != null ? `${totalRate}%` : '—'}
          sub={`${stats?.all?.failed ?? 0} échecs`}
          icon={ChartLine}
          color={totalRate != null && totalRate < 80 ? '#EF4444' : '#0052FF'}
          testid="kpi-rate"
        />
      </div>

      {/* Top failures */}
      {stats?.top_failures?.length > 0 && (
        <Card className="border border-red-200 bg-red-50/50 shadow-none" data-testid="notif-top-failures">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-outfit flex items-center gap-2 text-red-700">
              <Warning size={16} weight="duotone" /> Motifs d&apos;échec fréquents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-xs">
              {stats.top_failures.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="px-2 py-0.5 rounded bg-red-200 text-red-800 font-bold text-[10px] shrink-0 mt-0.5">
                    ×{f.count}
                  </span>
                  <span className="text-red-800 break-all">{f.reason}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-sm items-end">
            <div>
              <Label className="text-xs">Canal</Label>
              <div className="mt-1 grid grid-cols-3 gap-1 bg-[#F8FAFC] rounded p-0.5">
                {CHANNELS.map(c => (
                  <button
                    key={c.key || 'all'}
                    onClick={() => setFilters(f => ({ ...f, channel: c.key, skip: 0 }))}
                    data-testid={`filter-channel-${c.key || 'all'}`}
                    className={`py-1.5 px-1 rounded text-xs font-medium ${
                      filters.channel === c.key ? 'bg-[#0052FF] text-white' : 'text-[#64748B]'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <div className="mt-1 grid grid-cols-3 gap-1 bg-[#F8FAFC] rounded p-0.5">
                {STATUSES.map(s => (
                  <button
                    key={s.key || 'all'}
                    onClick={() => setFilters(f => ({ ...f, status: s.key, skip: 0 }))}
                    data-testid={`filter-status-${s.key || 'all'}`}
                    className={`py-1.5 px-1 rounded text-xs font-medium ${
                      filters.status === s.key ? 'bg-[#0052FF] text-white' : 'text-[#64748B]'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Événement</Label>
              <Input value={filters.event} onChange={e => setFilters(f => ({ ...f, event: e.target.value, skip: 0 }))}
                     placeholder="ex: appointment_pending" className="h-9 text-sm mt-1" data-testid="filter-event" />
            </div>
            <div>
              <Label className="text-xs">Destinataire</Label>
              <Input value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value, skip: 0 }))}
                     placeholder="email / téléphone" className="h-9 text-sm mt-1" data-testid="filter-to" />
            </div>
            <div>
              <Label className="text-xs">Du</Label>
              <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value, skip: 0 }))}
                     className="h-9 text-sm mt-1" data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-xs">Au</Label>
              <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value, skip: 0 }))}
                     className="h-9 text-sm mt-1" data-testid="filter-date-to" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs table */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="font-outfit text-base">
            {logs.total} entrée{logs.total > 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
            </div>
          ) : logs.items.length === 0 ? (
            <p className="p-6 text-center text-sm text-[#94A3B8]">Aucune notification pour ces filtres.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-[#F8FAFC] text-[#64748B] uppercase">
                <tr>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Canal</th>
                  <th className="text-left p-2">Destinataire</th>
                  <th className="text-left p-2">Sujet / Message</th>
                  <th className="text-left p-2">Événement</th>
                  <th className="text-left p-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {logs.items.map(l => (
                  <tr key={l.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`notif-row-${l.id}`}>
                    <td className="p-2 whitespace-nowrap text-[#64748B]">{l.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        l.channel === 'email' ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#FEF3C7] text-[#B45309]'
                      }`}>{l.channel}</span>
                    </td>
                    <td className="p-2 font-mono text-[10px] break-all max-w-[160px]">{l.to}</td>
                    <td className="p-2 max-w-[380px]">
                      {l.subject && <div className="font-semibold truncate">{l.subject}</div>}
                      <div className="text-[#64748B] truncate">{l.body_preview}</div>
                      {l.error && <div className="text-red-600 text-[10px] mt-0.5 truncate">✗ {l.error}</div>}
                    </td>
                    <td className="p-2 text-[10px] text-[#64748B] break-all">{l.context?.event || '—'}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        l.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`} data-testid={`row-status-${l.id}`}>
                        {l.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {logs.total > logs.items.length + (filters.skip || 0) && (
            <div className="p-3 flex justify-between items-center border-t border-[#E2E8F0]">
              <span className="text-xs text-[#64748B]">
                {filters.skip + 1} — {filters.skip + logs.items.length} sur {logs.total}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={filters.skip === 0}
                        onClick={() => setFilters(f => ({ ...f, skip: Math.max(0, f.skip - f.limit) }))}
                        data-testid="notif-prev">← Précédent</Button>
                <Button size="sm" variant="outline"
                        onClick={() => setFilters(f => ({ ...f, skip: f.skip + f.limit }))}
                        data-testid="notif-next">Suivant →</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Retention / RGPD */}
      <Card className="border border-amber-200 bg-amber-50/40 shadow-none" data-testid="notif-retention">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-outfit flex items-center gap-2 text-amber-800">
            <Trash size={16} weight="duotone" /> Purge des anciens journaux (RGPD)
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Supprimer les logs plus vieux que</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="number" min="7" max="3650" value={retentionDays}
                     onChange={e => setRetentionDays(parseInt(e.target.value, 10) || 90)}
                     className="w-24 h-9 text-sm" data-testid="retention-days" />
              <span className="text-sm text-[#64748B]">jours</span>
            </div>
          </div>
          <Button variant="outline" onClick={runPrune} disabled={pruning}
                  className="border-red-500 text-red-600 hover:bg-red-50"
                  data-testid="retention-prune-btn">
            <Trash size={14} className="mr-2" /> {pruning ? '…' : 'Purger'}
          </Button>
          <p className="text-xs text-amber-800/80 flex-1 min-w-full md:min-w-0">
            Recommandation RGPD : conserver les journaux transactionnels 90 jours maximum.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const KpiCard = ({ label, value, sub, icon: Icon, color, testid }) => (
  <Card className="border border-[#E2E8F0] shadow-none" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase text-[#64748B] font-medium">{label}</span>
        <Icon size={16} weight="duotone" style={{ color }} />
      </div>
      <div className="font-outfit text-2xl font-bold" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

export default AdminNotifications;
